import { useEffect, useState, useCallback, useRef } from 'react'
import request from 'sb-request/graphql'

import useDeepMemo from './useDeepMemo'


export type useQueryOptionsType = {
  name?: string
  variables?: object
  withErrorModal?: boolean
  modifyResult?: (any) => any
  skip?: boolean // to skip fetching
  cache?: boolean // to cache request
}

type InitialStateType<T extends Object> = {
  isFetching: boolean
  data: T | null
  errors: object | null
}

type ReturnType<T extends Object> = InitialStateType<T> & {
  fetch: () => Promise<T>
}

const cacheStorage = {}


const useRecurlyQuery = <Variables, Payload, ModifiedResult>(query: string, options: useQueryOptionsType = {}): ReturnType<ModifiedResult> => {
  const initialState: InitialStateType<ModifiedResult> = { isFetching: !options.skip, data: null, errors: null }
  const [ state, setState ] = useState(initialState)

  const isFetched = useRef(options.skip)

  const updatedOptions = useDeepMemo(() => ({
    ...options, query,
  }), [ options, query ])

  const handleFetch = useCallback(() => {
    const { query, variables, name = 'noName', cache, ...requestOptions } = updatedOptions

    const cacheKey = cache ? JSON.stringify({ query, variables }) : null

    if (cacheKey && cacheStorage[cacheKey]) {
      const data = cacheStorage[cacheKey]

      setState({ data, errors: null, isFetching: false })

      return Promise.resolve(data)
    }

    // Skipped first setState to avoid extra render
    if (isFetched.current) {
      setState({ data: null, errors: null, isFetching: true })
    }
    else {
      isFetched.current = true
    }

    const body = {
      query,
      variables,
    } as any

    return request<Variables, Payload, ModifiedResult>(name, body, requestOptions)
      .then(
        (data) => {
          if (cacheKey) {
            cacheStorage[cacheKey] = data
          }

          return setState({ data, isFetching: false, errors: null })
        },
        ({ errors }) => setState({ errors, isFetching: false, data: null })
      )
  }, [ updatedOptions ])

  useEffect(() => {
    if (!updatedOptions.skip) {
      handleFetch()
    }
  }, [ handleFetch, updatedOptions.skip ])

  return { ...state, fetch: handleFetch }
}


export default useRecurlyQuery
